import {
  CheckmarkCircleRegular,
  PenSparkleRegular,
  QuestionCircleRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { TFunction } from 'i18next';
import { ReactElement } from 'react';
import {
  AzureGroupInfoResponse,
  LessonNameAndType,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { Popup } from '../Popup';

import { CourseMapping, CourseMappingDisplayModel, VerificationResult } from '..';
import {
  CourseMappingExportModel,
  CourseMappingUpdateModel,
  ImportIssue,
  withMnsProCourse,
} from './CourseMapping.interfaces';
import { ErrorType, MappingState } from './CourseMapping.enums';
import './CourseMappingComponent.scss';

export const getIconFromMappingState = ({
  state,
  t,
}: {
  state: MappingState;
  t: TFunction;
}): ReactElement => {
  let icon: ReactElement = <QuestionCircleRegular />;
  let popupText: string = t('Error', { defaultValue: 'Fehler' });
  switch (state) {
    case MappingState.AutoMapped:
      icon = (
        <PenSparkleRegular
          fontSize={20}
          className="CourseMappingComponent__icon-suggested"
          aria-label={t('mapping_state_suggested', { defaultValue: 'Vorgeschlagen' })}
        />
      );
      popupText = t('this_mapping_is_a_suggestion', {
        defaultValue:
          'Für diese Kurszuweisung liegt ein Vorschlag vor. Er wird erst gespeichert, wenn Sie ihn über das Häkchen übernehmen. Zum Verwerfen die Schaltfläche „×“ verwenden.',
      });
      break;
    case MappingState.ManuallyMapped:
      // A manually mapped course needs no explanatory tooltip: since the
      // id-based rework there is no longer an "automatically created" counterpart
      // to distinguish it from, so the hover popup is intentionally omitted.
      return (
        <CheckmarkCircleRegular
          fontSize={20}
          className="CourseMappingComponent__icon-manually-mapped"
          aria-label={t('mapping_state_manually_mapped', { defaultValue: 'Manuell zugewiesen' })}
        />
      );
    case MappingState.Unassigned:
      icon = (
        <WarningRegular
          fontSize={20}
          className="CourseMappingComponent__icon-unassigned"
          aria-label={t('mapping_state_unassigned', { defaultValue: 'Nicht zugewiesen' })}
        />
      );
      popupText = t('this_mapping_has_to_be_created', {
        defaultValue:
          'Diese Kurszuweisung ist unvollständig. Bitte entsprechende Gruppe per Dropdown auswählen.',
      });
      break;
  }

  return <Popup content={popupText} trigger={icon} on="hover" mouseLeaveDelay={0} />;
};

export const getAssignmentFromDisplayModel = (
  displayModel: CourseMappingDisplayModel,
): CourseMapping => {
  return {
    mappingState: displayModel.mappingState,
    mappingType: displayModel.mappingType,
    azureGroupId: displayModel.azureGroupId,
    azureGroupName: displayModel.azureGroupName,
    timetableCourse: displayModel.timetableCourse,
    suggestedAzureGroupId: displayModel.suggestedAzureGroupId,
    suggestedAzureGroupName: displayModel.suggestedAzureGroupName,
  } as CourseMapping;
};

const optionalAzureGroupIsValid = (
  azureGroupId: string | null,
  cloudCoursesPool: AzureGroupInfoResponse[],
) => {
  return !(!!azureGroupId && !cloudCoursesPool.some((group) => group.id === azureGroupId));
};

const createDisplayModelFromChanges = (
  originModel: CourseMappingDisplayModel,
  cloudCoursesPool: AzureGroupInfoResponse[],
  duplicateAzureGroupIds: Set<string>,
): CourseMappingDisplayModel => {
  const azureGroupId = originModel.azureGroupId;
  let error: ErrorType = ErrorType.none;

  if (!optionalAzureGroupIsValid(azureGroupId, cloudCoursesPool)) {
    error = ErrorType.cloudCourseError;
  } else if (azureGroupId && duplicateAzureGroupIds.has(azureGroupId)) {
    error = ErrorType.duplicateAzureGroup;
  }

  return {
    ...originModel,
    // Only an unresolvable group invalidates the selection itself. A duplicate is
    // a valid group in a conflicting place, so the row keeps its state and the
    // user can see (and fix) which side to clear.
    mappingState:
      error === ErrorType.cloudCourseError && azureGroupId
        ? MappingState.Unassigned
        : originModel.mappingState,
    error,
  };
};

/**
 * Collects the applied group ids that more than one row wants. Only the applied
 * `azureGroupId` counts — an unconfirmed suggestion is not an assignment yet and
 * must not raise an error the user cannot act on.
 */
const findDuplicateAzureGroupIds = (
  courseAssignments: CourseMappingDisplayModel[],
): Set<string> => {
  const counts: Record<string, number> = {};

  courseAssignments.forEach((assignment) => {
    if (assignment.azureGroupId) {
      counts[assignment.azureGroupId] = (counts[assignment.azureGroupId] ?? 0) + 1;
    }
  });

  return new Set(Object.keys(counts).filter((groupId) => counts[groupId] > 1));
};

/**
 * Validates the mappings against the Azure group pool. A mapping whose
 * `azureGroupId` is set but not found in the pool is flagged with
 * {@link ErrorType.cloudCourseError} and forced back to
 * {@link MappingState.Unassigned}.
 *
 * Unless `allowMultipleAzureGroupUsage` is set, every group may only be held by
 * one entity: rows sharing an applied group are flagged with
 * {@link ErrorType.duplicateAzureGroup}, which blocks the save. This is the
 * single gate for all input paths — manual selection, confirmed suggestions and
 * CSV import all funnel through here (Bug #11517).
 *
 * Note: the former name-based verification (invalid / to-be-deleted timetable
 * courses, driven by the removed CSV import) is intentionally gone — mappings
 * are keyed by `entityRevisionsId` and only ever originate from the timetable
 * course pool or the backend, so those states can no longer occur.
 */
export const verifyMappings = (
  courseAssignments: CourseMappingDisplayModel[],
  cloudCoursesPool: AzureGroupInfoResponse[],
  allowMultipleAzureGroupUsage = false,
): VerificationResult => {
  const duplicateAzureGroupIds = allowMultipleAzureGroupUsage
    ? new Set<string>()
    : findDuplicateAzureGroupIds(courseAssignments);

  const takenAzureGroupIds = allowMultipleAzureGroupUsage
    ? new Set<string>()
    : findAppliedAzureGroupIds(courseAssignments);

  const verifiedMappings = courseAssignments
    .map((assignment) => dropUnusableSuggestion(assignment, takenAzureGroupIds))
    .map((assignment) =>
      createDisplayModelFromChanges(assignment, cloudCoursesPool, duplicateAzureGroupIds),
    );

  const hasError = verifiedMappings.some(
    (mapping) => mapping.error !== undefined && mapping.error > ErrorType.none,
  );

  return {
    hasError,
    verifiedMappings,
  };
};

const findAppliedAzureGroupIds = (
  courseAssignments: CourseMappingDisplayModel[],
): Set<string> =>
  new Set(
    courseAssignments
      .filter((assignment) => assignment.azureGroupId)
      .map((assignment) => assignment.azureGroupId as string),
  );

/**
 * Hides a suggestion the user could not act on: the suggested group is already
 * applied to another row, so confirming it would only produce the duplicate the
 * backend rejects. The row falls back to `Unassigned` with an empty combobox
 * instead of showing a group that looks assigned but is not (Bug #11517).
 *
 * Suggestions are recomputed server-side on every load and never persisted, so
 * dropping one here costs nothing.
 */
const dropUnusableSuggestion = (
  assignment: CourseMappingDisplayModel,
  takenAzureGroupIds: Set<string>,
): CourseMappingDisplayModel =>
  !assignment.azureGroupId
  && assignment.suggestedAzureGroupId
  && takenAzureGroupIds.has(assignment.suggestedAzureGroupId)
    ? {
        ...assignment,
        mappingState: MappingState.Unassigned,
        suggestedAzureGroupId: null,
        suggestedAzureGroupName: null,
      }
    : assignment;

/**
 * Selects the mappings whose applied `azureGroupId` differs from the
 * initially-loaded value (both normalized to `null`), keyed by
 * `timetableCourse.entityRevisionsId`.
 *
 * This intentionally is **not** limited to `MappingState.ManuallyMapped` rows:
 * an assignment the user clears goes back to `Unassigned` with
 * `azureGroupId: null`, but that clear must still be reported so the backend
 * can remove the previously-applied group membership. A lesson that has no
 * entry in `initialMappings` (e.g. newly imported) is treated as initially
 * unassigned.
 */
export const selectChangedMappings = (
  workingMappings: CourseMapping[],
  initialMappings: CourseMapping[],
): CourseMapping[] =>
  workingMappings.filter((mapping) => {
    const initialMapping = initialMappings.find(
      (candidate) =>
        candidate.timetableCourse.entityRevisionsId === mapping.timetableCourse.entityRevisionsId,
    );
    return (initialMapping?.azureGroupId ?? null) !== (mapping.azureGroupId ?? null);
  });

export const downloadCSV = (
  fileName: string,
  dataJson: string,
  unparseConfig: unknown,
  parseFunction: (json: unknown, options?: unknown) => string,
) => {
  const csv = parseFunction(dataJson, unparseConfig);
  const anchor = document.createElement('a');
  const isWindows = navigator.userAgent.includes('Windows');
  if (isWindows) {
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    anchor.href = URL.createObjectURL(blob);
  } else {
    anchor.href = 'data:attachment/csv, ﻿' + encodeURIComponent(csv);
  }
  anchor.download = `${fileName}.csv`;
  anchor.click();
};

export const buildAssignmentExport = (
  mappings: CourseMapping[],
): CourseMappingExportModel[] =>
  mappings.map((mapping) => ({
    mnsproCourse: mapping.azureGroupName,
    timetableCourse: mapping.timetableCourse.lessonName,
    timetableCourseGroupType: mapping.timetableCourse.groupType,
  }));

export const buildGroupsExport = (
  cloudCourses: AzureGroupInfoResponse[],
): withMnsProCourse[] =>
  cloudCourses.map((group) => ({ mnsproCourse: group.displayName ?? null }));

/**
 * Builds the combobox label for each Azure group, keyed by group id. When a
 * display name is shared by more than one group it is ambiguous on its own, so
 * the group's mail address is appended in round brackets (`Name (mail)`) to
 * tell the duplicates apart. Unique names — and duplicates without a mail
 * address to disambiguate with — keep the plain display name.
 *
 * This is a display-only concern: the returned label must never be persisted as
 * `azureGroupName`, which stays the plain display name so CSV export/import
 * (matched by display name) keeps working.
 */
export const buildAzureGroupOptionLabels = (
  groups: AzureGroupInfoResponse[],
): Map<string, string> => {
  const nameCounts = new Map<string, number>();
  groups.forEach((group) => {
    const name = group.displayName ?? '';
    nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  });

  const labels = new Map<string, string>();
  groups.forEach((group) => {
    const name = group.displayName ?? '';
    const mail = group.mail ?? '';
    const isDuplicateName = (nameCounts.get(name) ?? 0) > 1;
    labels.set(group.id, isDuplicateName && mail.length > 0 ? `${name} (${mail})` : name);
  });

  return labels;
};

/**
 * Turns raw, name-based CSV rows into id-based update models. A row is applied
 * only when its lesson resolves (by lessonName + groupType) and its group name
 * resolves to exactly one Azure group; every other row is reported as an
 * {@link ImportIssue} and left unapplied. An empty group name clears the
 * assignment.
 */
export const resolveImportedMappings = (
  rows: CourseMappingExportModel[],
  timetableCourses: LessonNameAndType[],
  cloudCourses: AzureGroupInfoResponse[],
): { updates: CourseMappingUpdateModel[]; issues: ImportIssue[] } => {
  const updates: CourseMappingUpdateModel[] = [];
  const issues: ImportIssue[] = [];

  rows.forEach((row) => {
    const lesson = timetableCourses.find(
      (course) =>
        course.lessonName === row.timetableCourse
        && course.groupType === row.timetableCourseGroupType,
    );

    if (!lesson) {
      issues.push({ kind: 'unknownLesson', timetableCourse: row.timetableCourse, value: row.mnsproCourse });
      return;
    }

    const groupName = (row.mnsproCourse ?? '').trim();

    if (groupName.length === 0) {
      updates.push({ timetableCourse: lesson, azureGroupId: null, azureGroupName: null });
      return;
    }

    const matches = cloudCourses.filter((group) => (group.displayName ?? '') === groupName);

    if (matches.length === 0) {
      issues.push({ kind: 'groupNotFound', timetableCourse: lesson.lessonName, value: groupName });
      return;
    }

    if (matches.length > 1) {
      issues.push({ kind: 'ambiguousGroup', timetableCourse: lesson.lessonName, value: groupName });
      return;
    }

    updates.push({
      timetableCourse: lesson,
      azureGroupId: matches[0].id,
      azureGroupName: matches[0].displayName,
    });
  });

  return { updates, issues };
};
